import * as React from 'react'
import {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react'

export interface VerticalPositionContextType {
  centerY: number | null
  onSelectItem: (el: HTMLElement | null, id: string | null) => void
  setContainerRef: (el: HTMLElement | null) => void
}

export const VerticalPositionContext = createContext<VerticalPositionContextType | null>(null)

export function VerticalPositionProvider({children}: {children: React.ReactNode}) {
  // Vertical center of the current selected item
  const [centerY, setCenterY] = useState<number | null>(null)
  const selectedItemRef = useRef<HTMLElement | null>(null)

  // Updates centerY whenever the size of the container DOM element changes
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(entries => {
        if (!selectedItemRef.current) return
        const boundingRect = selectedItemRef.current.getBoundingClientRect()
        const centerYValue = boundingRect.top + boundingRect.height / 2
        setCenterY(centerYValue)
      }),
    []
  )

  const onSelectItem = useCallback((el: HTMLElement | null, id: string | null) => {
    // Deselect everything
    if (!el || el === selectedItemRef.current) {
      setCenterY(null)
      selectedItemRef.current = null
      return
    }
    // Initially set the position and update the ref for the observer
    const boundingRect = el.getBoundingClientRect()
    const centerYValue = boundingRect.top + boundingRect.height / 2
    setCenterY(centerYValue)
    selectedItemRef.current = el
  }, [])

  const setContainerRef = useCallback((target: HTMLElement | null) => {
    if (target) {
      resizeObserver.observe(target)
    }
  }, [])

  useEffect(() => {
    return () => resizeObserver.disconnect()
  }, [])
  return (
    <VerticalPositionContext.Provider value={{centerY, onSelectItem, setContainerRef}}>
      {children}
    </VerticalPositionContext.Provider>
  )
}
